import type { ReactNode } from "react";
import { Form, FormField, FormInput, Button } from "web-design-system";
import { useForm } from "react-hook-form";
import { useNavigate } from "@tanstack/react-router";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  useDistributorEmployeeVerificationLogin,
  useSetPasswordDistributorWithToken,
} from "creso-core/hooks";
import { ArrowLeftIcon, Loader2Icon } from "lucide-react";
import { handleError } from "@/utils/error";
import { AuthSplitLayout } from "../components/auth-split-layout";

const passwordSchema = z
  .object({
    password: z.string().min(1, "Password is required"),
    confirmPassword: z.string().min(1, "Confirm Password is required"),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Passwords do not match",
    path: ["confirmPassword"],
  });

type Props = {
  verificationToken: string;
};

export default function SetPassword({ verificationToken }: Props) {
  const navigate = useNavigate();
  const setPassword = useSetPasswordDistributorWithToken();
  const verificationLogin =
    useDistributorEmployeeVerificationLogin(verificationToken);

  const form = useForm<z.infer<typeof passwordSchema>>({
    resolver: zodResolver(passwordSchema),
    defaultValues: {
      password: "",
      confirmPassword: "",
    },
  });

  const onSubmit = async (data: z.infer<typeof passwordSchema>) => {
    try {
      await setPassword.mutateAsync({
        new_password: data.password,
      });
      navigate({ to: "/" });
    } catch (error) {
      handleError(
        error,
        "Could not set password. Please try again or sign in manually."
      );
    }
  };

  const goToLogin = () => {
    navigate({ to: "/login/distributor" });
  };

  const tokenMissing = !verificationToken.trim();

  let main: ReactNode;
  if (tokenMissing) {
    main = (
      <div className="flex flex-col gap-4 flex-1 pt-4">
        <span className="text-sm text-muted-foreground">
          This page needs a valid link with a verification token. Check your
          email or ask your admin to resend the invite.
        </span>
        <Button className="w-full mt-5" size="lg" onClick={goToLogin}>
          Back to login
        </Button>
      </div>
    );
  } else if (verificationLogin.isError) {
    main = (
      <div className="flex flex-col gap-4 flex-1 pt-4">
        <span className="text-sm text-muted-foreground">
          This link may be invalid or expired. Ask your admin to resend the
          invite or try signing in if you already have an account.
        </span>
        <Button className="w-full mt-5" size="lg" onClick={goToLogin}>
          Back to login
        </Button>
      </div>
    );
  } else if (verificationLogin.isSuccess) {
    main = (
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(onSubmit)}
          className="flex-1 flex flex-col gap-6"
        >
          <div className="flex flex-col gap-6 pb-8 flex-1">
            <span className="text-sm text-muted-foreground">
              Choose a password for your distributor account.
            </span>
            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormInput
                  {...field}
                  label="Password"
                  type="password"
                  className="gap-2"
                />
              )}
            />
            <FormField
              control={form.control}
              name="confirmPassword"
              render={({ field }) => (
                <FormInput
                  {...field}
                  label="Confirm Password"
                  type="password"
                  className="gap-2"
                />
              )}
            />
          </div>
          <Button
            className="w-full mt-5"
            size="lg"
            type="submit"
            disabled={setPassword.isPending}
          >
            {setPassword.isPending ? "Saving…" : "Set password"}
          </Button>
        </form>
      </Form>
    );
  } else {
    main = (
      <div className="flex flex-1 flex-col items-center justify-center gap-3 text-muted-foreground py-60 w-full">
        <Loader2Icon className="size-8 animate-spin" aria-hidden />
        <span className="text-sm">Signing you in…</span>
      </div>
    );
  }

  return (
    <AuthSplitLayout>
      <div className="flex flex-col pb-8 justify-start items-start w-full">
        <div className="flex items-center gap-3">
          <ArrowLeftIcon
            onClick={goToLogin}
            className="size-5 hover:scale-110 transition-all duration-300 cursor-pointer"
          />
          <span className="text-2xl font-semibold">Set password</span>
        </div>
        {main}
      </div>
    </AuthSplitLayout>
  );
}
